import { User, Exercise, ExerciseRow, Plan, PlanInput, PoolLocation, BulkExerciseResult, Auth0Profile } from "../types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataAPI } from "../providers/data-api-provider";
import { toast } from "react-hot-toast";

export const useGetAllUsers = () => {
  const apiClient = useDataAPI();

  return useQuery<User, Error>({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await apiClient.instance.get(`users`);

      return response.data;
    },
  });
};

export const useGetProfilesByRole = (role: "trainer" | "trainee") => {
  const apiClient = useDataAPI();

  return useQuery<Auth0Profile[], Error>({
    queryKey: ["profiles", role],
    queryFn: async () => {
      const response = await apiClient.instance.get("/profiles", { params: { role } });
      return response.data;
    },
  });
};

export const useGetUserById = ({ userId }: { userId: string }) => {
  const apiClient = useDataAPI();

  return useQuery<User, Error>({
    queryKey: ["users", userId],
    queryFn: async () => {
      const response = await apiClient.instance.get(`users/${userId}`);

      return response.data;
    },
    retry: 0, // Disable automatic retries
  });
};

export const useUpdateUser = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient(); // Get the query client

  return useMutation({
    mutationFn: async ({
      data,
      userId,
    }: {
      data: Partial<User>;
      userId: string;
    }) => {
      const response = await apiClient.instance.put(`/users/${userId}`, data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the query so it will refetch the data on next access
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Account details updated successfully!");
    },
    onError: (error) => {
      console.error("Error updating account data:", error);
      toast.error("Failed to update account details. Please try again.");
    },
  });
};

export const useCreateExercise = () => {
  const apiClient = useDataAPI();

  return useMutation({
    mutationFn: async ({
      exerciseData,
      planData,
    }: {
      exerciseData: Exercise;
      planData: PlanInput;
    }) => {
      // Step 1: Create exercise
      const exerciseResponse = await apiClient.instance.post(
        "/exercise",
        exerciseData
      );
      const createdExercise = exerciseResponse.data;

      if (!createdExercise?.id) {
        throw new Error("Failed to get Exercise ID");
      }

      // Step 2: Create plan with the new exercise ID
      const planPayload = {
        ...planData,
        exercisesIds: [createdExercise.id],
      };

      const planResponse = await apiClient.instance.post("/plan", planPayload);
      const createdPlan = planResponse.data;

      return { exercise: createdExercise, plan: createdPlan };
    },

    onSuccess: () => {
      toast.success("Exercise and plan created successfully!");
    },

    onError: (error) => {
      console.error("Error creating exercise or plan:", error);
      toast.error("Failed to create exercise or plan.");
    },
  });
};

export const useGetAllExercises = () => {
  const apiClient = useDataAPI();

  return useQuery<ExerciseRow[], Error>({
    queryKey: ["exercises"],
    queryFn: async () => {
      const response = await apiClient.instance.get("/exercises");
      return response.data;
    },
  });
};

export const useUploadImage = () => {
  const apiClient = useDataAPI();

  return useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const response = await apiClient.instance.post<string>("/upload/image", file, {
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      return response.data;
    },
    onError: () => {
      toast.error("Failed to upload image.");
    },
  });
};

export const useCreateExerciseOnly = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exerciseData: Exercise) => {
      const response = await apiClient.instance.post("/exercise", exerciseData);
      return response.data as ExerciseRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercises"] });
      toast.success("Exercise created successfully!");
    },
    onError: () => {
      toast.error("Failed to create exercise.");
    },
  });
};

export const useCreateExercisesBulk = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exercises: Exercise[]) => {
      const response = await apiClient.instance.post("/exercises/bulk", exercises);
      return response.data as BulkExerciseResult[];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercises"] });
    },
    onError: () => {
      toast.error("Failed to create exercises.");
    },
  });
};

export const useCreatePlanOnly = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PlanInput & { exercisesIds: string[] }) => {
      const response = await apiClient.instance.post("/plan", payload);
      return response.data as Plan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      toast.success("Plan created successfully!");
    },
    onError: () => {
      toast.error("Failed to create plan.");
    },
  });
};

export const useUpdateExercise = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Exercise }) => {
      const response = await apiClient.instance.put(`/exercise/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercises"] });
      toast.success("Exercise updated successfully!");
    },
    onError: () => {
      toast.error("Failed to update exercise.");
    },
  });
};

export const useUpdatePlan = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: object }) => {
      const response = await apiClient.instance.put(`/plan/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      toast.success("Plan updated successfully!");
    },
    onError: () => {
      toast.error("Failed to update plan.");
    },
  });
};

export const useDeleteExercise = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exerciseId: string) => {
      await apiClient.instance.delete(`/exercise/${exerciseId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercises"] });
      toast.success("Exercise deleted successfully!");
    },
    onError: () => {
      toast.error("Failed to delete exercise.");
    },
  });
};

export const useDeleteExercisesBulk = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (exerciseIds: string[]) => {
      await Promise.all(exerciseIds.map((exerciseId) => apiClient.instance.delete(`/exercise/${exerciseId}`)));
    },
    onSuccess: (_data, exerciseIds) => {
      queryClient.invalidateQueries({ queryKey: ["exercises"] });
      toast.success(`${exerciseIds.length} exercises deleted successfully!`);
    },
    onError: () => {
      toast.error("Failed to delete selected exercises.");
    },
  });
};

export const useDeletePlan = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planId: string) => {
      await apiClient.instance.delete(`/plan/${planId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      toast.success("Plan deleted successfully!");
    },
    onError: () => {
      toast.error("Failed to delete plan.");
    },
  });
};

export const useDeletePlansBulk = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planIds: string[]) => {
      await Promise.all(planIds.map((planId) => apiClient.instance.delete(`/plan/${planId}`)));
    },
    onSuccess: (_data, planIds) => {
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      toast.success(`${planIds.length} plans deleted successfully!`);
    },
    onError: () => {
      toast.error("Failed to delete selected plans.");
    },
  });
};

export const useGetAllPoolLocations = () => {
  const apiClient = useDataAPI();

  return useQuery<PoolLocation[], Error>({
    queryKey: ["pool-locations"],
    queryFn: async () => {
      const response = await apiClient.instance.get("/pool-locations");
      return response.data;
    },
  });
};

export const useCreatePoolLocation = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Omit<PoolLocation, "id">) => {
      const response = await apiClient.instance.post("/pool-locations", data);
      return response.data as PoolLocation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pool-locations"] });
      toast.success("Pool created successfully!");
    },
    onError: () => {
      toast.error("Failed to create pool.");
    },
  });
};

export const useUpdatePoolLocation = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PoolLocation> }) => {
      const response = await apiClient.instance.put(`/pool-locations/${id}`, data);
      return response.data as PoolLocation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pool-locations"] });
      toast.success("Pool updated successfully!");
    },
    onError: () => {
      toast.error("Failed to update pool.");
    },
  });
};

export const useDeletePoolLocation = () => {
  const apiClient = useDataAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.instance.delete(`/pool-locations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pool-locations"] });
      toast.success("Pool deleted successfully!");
    },
    onError: () => {
      toast.error("Failed to delete pool.");
    },
  });
};

export const useGetAllPlans = () => {
  const apiClient = useDataAPI();

  return useQuery<Plan[], Error>({
    queryKey: ["plans"], // Unique key to identify the query
    queryFn: async () => {
      const response = await apiClient.instance.get("/plans");
      return response.data;
    },
  });
};
